import { jourTravaille } from './semaine';
import { trf } from './i18n';

export const _seedISO = (o = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + o);
  return d.toISOString().slice(0, 10);
};

export const _seedFR = (o = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + o);
  return (
    String(d.getDate()).padStart(2, '0') +
    '.' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '.' +
    d.getFullYear()
  );
};

export const _parseDateFR = (s) => {
  const m = String(s || '').match(/(\d{1,2})[./](\d{1,2})[./](\d{2,4})/);
  if (!m) return null;
  let y = +m[3];
  if (y < 100) y += 2000;
  return new Date(y, +m[2] - 1, +m[1]);
};

/**
 * Jours d'une échéance écrite (« 30 jours »). « À réception » vaut 0 : sans
 * chiffre à lire, elle retombait sur trente jours, et une facture payable à
 * réception n'était signalée en retard qu'un mois plus tard.
 *
 * @param {string} ech échéance telle que l'écran l'écrit
 * @returns {number} les jours, 30 à défaut
 */
export const echeanceJours = (ech) => {
  if (/r[ée]ception/i.test(String(ech || ''))) return 0;
  const m = String(ech || '').match(/(\d+)/);
  return m ? +m[1] : 30;
};

export const joursRetard = (f) => {
  const d = _parseDateFR(f && f.date);
  if (!d) return 0;
  const due = new Date(d);
  due.setDate(due.getDate() + echeanceJours(f && f.echeance));
  return Math.floor((Date.now() - due.getTime()) / 86400000);
};

export const joursDepuis = (dateStr) => {
  const d = _parseDateFR(dateStr);
  return d ? Math.floor((Date.now() - d.getTime()) / 86400000) : 0;
};

export const dateEcheance = (f) => {
  const d = _parseDateFR(f && f.date);
  if (!d) return '';
  const due = new Date(d);
  due.setDate(due.getDate() + echeanceJours(f && f.echeance));
  return (
    String(due.getDate()).padStart(2, '0') +
    '.' +
    String(due.getMonth() + 1).padStart(2, '0') +
    '.' +
    due.getFullYear()
  );
};

/**
 * Rend une date ISO à la suisse : `2026-09-21` devient `21.09.2026`.
 *
 * <p>Les dates venues du serveur sont ISO ; celles que les écrans manipulent
 * sont suisses. Affichée telle quelle, une date du serveur détonnait au milieu
 * des autres — et dans l'espace de l'ouvrier, la liste de ses journées était
 * la seule à s'écrire à l'envers.
 *
 * @param {string} iso date au format `AAAA-MM-JJ`
 * @returns {string} la date au format `JJ.MM.AAAA`, ou la valeur reçue
 */
export function dateSuisse(iso) {
  if (!iso) return '';
  // Un horodatage (« 2026-10-10T12:49:20.466Z ») s'écrivait
  // « 10T12:49:20.466788Z.10.2026 » sous les messages : il prend sa date et
  // son heure, à l'heure suisse.
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(iso))) return dateHeureSuisse(iso);
  const parts = String(iso).split('-');
  return parts.length === 3 ? parts[2] + '.' + parts[1] + '.' + parts[0] : String(iso);
}

/**
 * Rend un horodatage du serveur en date et heure suisses, à l'heure locale :
 * `2026-10-10T12:49:20Z` devient `10.10.2026 à 14:49` en été.
 *
 * @param {string} iso horodatage ISO
 * @returns {string} « JJ.MM.AAAA à HH:MM », ou la valeur reçue si elle ne se lit pas
 */
export function dateHeureSuisse(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const deux = (n) => String(n).padStart(2, '0');
  // « à » se dit dans la langue de l'écran : « um » en allemand.
  return trf(
    '{0} à {1}',
    deux(d.getDate()) + '.' + deux(d.getMonth() + 1) + '.' + d.getFullYear(),
    deux(d.getHours()) + ':' + deux(d.getMinutes()),
  );
}

/**
 * Un jour ouvrable : du lundi au vendredi en Suisse, selon le week-end du
 * pays ailleurs (lib/semaine.js). Les jours de repos ne comptent ni dans les
 * vacances ni dans les heures prévues au contrat.
 *
 * @param {string} jour date ISO (`AAAA-MM-JJ`) ou clé `AAAAMMJJ`
 * @returns {boolean} vrai du lundi au vendredi
 */
export function estJourOuvrable(jour) {
  const s = String(jour || '').replace(/-/g, '');
  if (!/^\d{8}$/.test(s)) return true;
  const j = new Date(+s.slice(0, 4), +s.slice(4, 6) - 1, +s.slice(6, 8), 12).getDay();
  return jourTravaille(j);
}

/**
 * Jours ouvrables (lundi au vendredi, ou selon le pays) entre deux dates ISO,
 * bornes comprises.
 *
 * @param {string} debut premier jour, `AAAA-MM-JJ`
 * @param {string} fin dernier jour, `AAAA-MM-JJ`
 * @returns {number|null} le nombre de jours, ou null si les dates ne se lisent pas
 */
export function joursOuvres(debut, fin) {
  if (!debut || !fin || fin < debut) return null;
  const jour = new Date(debut + 'T12:00:00');
  const dernier = new Date(fin + 'T12:00:00');
  if (isNaN(jour) || isNaN(dernier)) return null;
  let n = 0;
  while (jour <= dernier) {
    if (jourTravaille(jour.getDay())) n += 1;
    jour.setDate(jour.getDate() + 1);
  }
  return n;
}
